import React, { forwardRef, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { css, cx } from '@emotion/css';
import { usePopper } from 'react-popper';
import { theme, useThemeContext, getThemeColor, } from '@contrast-security-inc/design-system-foundations';
import mergeRefs from 'merge-refs';
const useClickOutside = (refs, callback, enabled) => {
    useEffect(() => {
        const handleClick = (event) => {
            if (event.target?.isConnected && refs?.every((ref) => !ref?.contains(event.target))) {
                callback();
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                callback();
            }
        };
        if (enabled) {
            document.addEventListener('click', handleClick);
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('click', handleClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
        // I know the callback won't change after component initialization
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, refs]);
};
const ARROW_SIZE = 15;
const POPPER_PLACEMENT = -ARROW_SIZE / 2.0;
const DEFAULT_OFFSET = ARROW_SIZE;
const arrowStyles = (selectedTheme) => css `
  position: absolute;
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  background: inherit;
  visibility: hidden;

  &::after {
    position: absolute;
    width: ${ARROW_SIZE}px;
    height: ${ARROW_SIZE}px;
    background: inherit;
    visibility: visible;
    content: "";
    transform: rotate(45deg);
    border: 1px solid ${getThemeColor(selectedTheme, { light: theme.color.neutral40, dark: theme.color.neutral70 })};
    z-index: -1;
  }
`;
const containerStyles = (zIndex, width, height, selectedTheme, popperPlacement = POPPER_PLACEMENT) => css `
  border: 1px solid ${getThemeColor(selectedTheme, { light: theme.color.neutral40, dark: theme.color.neutral70 })};
  background-color: ${getThemeColor(selectedTheme, { light: theme.color.white, dark: theme.color.black })};
  border-radius: 4px;
  box-shadow: ${theme.shadow.md};
  z-index: ${zIndex};
  ${width && `width: ${width}px`};
  ${height && `height: ${height}px`};

  &[data-popper-placement^="top"] > span {
    bottom: ${popperPlacement + 0.06 * ARROW_SIZE}px;
  }

  &[data-popper-placement^="bottom"] > span {
    top: ${popperPlacement - 0.06 * ARROW_SIZE}px;
  }

  &[data-popper-placement^="left"] > span {
    right: ${popperPlacement + 0.06 * ARROW_SIZE}px;
  }

  &[data-popper-placement^="right"] > span {
    left: ${popperPlacement - 0.06 * ARROW_SIZE}px;
  }
`;
const contentStyles = (selectedTheme) => css `
  position: relative;
  z-index: 1;
  background-color: ${getThemeColor(selectedTheme, { light: theme.color.white, dark: theme.color.black })};
  border-radius: 4px;
  width: 100%;
  height: 100%;
`;
export const Popover = forwardRef(({ children, className, hasArrow, height, isOpen, offset = [0, DEFAULT_OFFSET], placement = 'bottom', referenceElement, testId, width, onClose, style: userStyles, zIndex = theme.zIndex.popover, ...rest }, ref) => {
    const { selectedTheme } = useThemeContext();
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const resizeObserver = useRef(null);
    const { styles, attributes, forceUpdate } = usePopper(referenceElement, popperElement, {
        placement,
        modifiers: [
            { name: 'arrow', options: { element: arrowElement } },
            { name: 'offset', options: { offset } },
        ],
    });
    //In the future this could be a custom hook to handle the resizeObserver
    useEffect(() => {
        if (!resizeObserver.current && forceUpdate) {
            // This is a workaround for a bug in react-popper where the popper element placement is not updated when the children's width change after the first render
            // references to the issue: https://contrast.atlassian.net/browse/TS-21025
            resizeObserver.current = new ResizeObserver(() => {
                forceUpdate?.();
            });
        }
        if (popperElement && resizeObserver.current) {
            resizeObserver.current.observe(popperElement);
        }
        return () => {
            if (popperElement) {
                resizeObserver.current?.unobserve(popperElement);
            }
        };
    }, [popperElement, forceUpdate]);
    useClickOutside([popperElement, referenceElement], () => {
        onClose?.(false);
    }, isOpen);
    const classes = cx(containerStyles(zIndex, width, height, selectedTheme), className, 'ds-popover');
    return (React.createElement(React.Fragment, null, isOpen &&
        ReactDOM.createPortal(React.createElement("div", { "data-popper-placement": placement, className: classes, ref: mergeRefs(setPopperElement, ref), style: {
                ...styles.popper,
                ...userStyles,
            }, "data-testid": testId, ...attributes.popper, ...rest },
            React.createElement("div", { className: contentStyles(selectedTheme), "data-testid": testId && `${testId}-content` }, children),
            hasArrow && (React.createElement("span", { className: arrowStyles(selectedTheme), ref: setArrowElement, "data-testid": testId && `${testId}-arrow`, style: styles.arrow }))), document.body)));
});
Popover.displayName = 'Popover';
